<template>
  <div class="d-flex flex-column gap-4">
    <p class="py-6" v-html="t('unitLimiter.unitLimitSettings.controlDescription')"></p>

    <div>
      <LimiterCard
        v-for="limiter in limiters"
        :key="limiter.type"
        :limiter="limiter"
        @click="limiter.onClick" />
    </div>

    <div
      v-if="!areHandlingUnitsEnabled"
      data-testid="handling-units-disabled-info"
      class="px-5 py-2 border border-radius bg-disabled typography-body-sm text-tertiary">
      <strong>
        <v-icon size="x-small">mdi-help-circle-outline</v-icon>
        {{ t('unitLimiter.limiterCard.howToEnableHandlingUnits') }}
      </strong>

      <p v-html="t('unitLimiter.limiterCard.includeHandlingUnitsFieldInstructions') + '.'"></p>
    </div>

    <div>
      <v-divider />

      <!-- Override settings -->
      <div class="d-flex flex-column gap-2 mb-4 mt-4">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-cog-outline</v-icon>
          <p class="font-weight-bold">{{ t('unitLimiter.limiterSettings.title') }}</p>
        </div>

        <p>
          {{ t('unitLimiter.limiterSettings.description') }}
        </p>

        <v-switch
          v-model="isOverrideLimiterSettingEnabled"
          :label="t('unitLimiter.limiterSettings.enableOverride')"
          :disabled="readOnly"
          hide-details />

        <div class="typography-body-sm ml-12 text-tertiary">
          {{ t('unitLimiter.limiterSettings.helperTextPrefix') }}
          <strong>{{ t('unitLimiter.limiterSettings.helperTextBold') }}</strong>
          {{ t('unitLimiter.limiterSettings.helperTextSuffix') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LimiterCard from './LimiterCard.vue';
import { UnitType } from '@nova/core';

const router = useRouter();
const { t } = useI18n();

const props = defineProps({
  warehouse: {
    type: Object,
    required: true
  },
  entitySettings: {
    type: Object,
    required: true
  },
  /** Disables the override switch. Default false keeps the legacy page unchanged. */
  readOnly: {
    type: Boolean,
    required: false,
    default: false
  }
});

const warehouseSettings = props.warehouse.settings || {};

const isOverrideLimiterSettingEnabled = computed({
  get() {
    return props.entitySettings.enableOverrideLimiterFunction === true;
  },
  set(value) {
    props.entitySettings.enableOverrideLimiterFunction = value;
  }
});

const areHandlingUnitsEnabled = computed(() =>
  Boolean(warehouseSettings.includeHandlingUnitsFieldOnAppointmentForms)
);

const onClickAppointments = () => {
  router.push({
    name: 'warehouses.details.settings',
    query: { active: 'limits', limitType: UnitType.Appointment }
  });
};

const onClickHandlingUnits = () => {
  if (areHandlingUnitsEnabled.value) {
    router.push({
      name: 'warehouses.details.settings',
      query: { active: 'limits', limitType: UnitType.Units }
    });
  }
};

const limiters = computed(() => [
  {
    type: 'appointment',
    title: t('unitLimiter.limiterCard.appointmentLimiter'),
    subtitle: null,
    enabled: true,
    buttonText: t('unitLimiter.limiterCard.manage'),
    showIcon: true,
    onClick: onClickAppointments
  },
  {
    type: 'handlingUnit',
    title: t('unitLimiter.limiterCard.handlingUnitsLimiter'),
    subtitle: warehouseSettings.handlingUnitsLabel,
    enabled: areHandlingUnitsEnabled.value,
    showIcon: true,
    onClick: onClickHandlingUnits
  }
]);
</script>
